import React, { createContext, useContext, useState, useCallback } from "react";
import { Alert, AlertType, AlertProps } from "../components/ui/alert";

interface AlertConfig {
  type: AlertType;
  message: string;
  link?: {
    text: string;
    href: string;
    onClick?: () => void;
  };
  duration?: number;
}

interface AlertContextType {
  showAlert: (config: AlertConfig) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

const DEFAULT_DURATION = 5000; // 5 seconds

export function AlertProvider({ children }: { children: React.ReactNode }) {
  const [alerts, setAlerts] = useState<AlertProps[]>([]);

  const removeAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const showAlert = useCallback(
    (config: AlertConfig) => {
      const id = `alert-${Date.now()}-${Math.random()}`;
      const duration = config.duration ?? DEFAULT_DURATION;

      const newAlert: AlertProps = {
        id,
        type: config.type,
        message: config.message,
        link: config.link,
        onClose: removeAlert,
      };

      setAlerts((prev) => [...prev, newAlert]);

      // Auto-dismiss after duration
      if (duration > 0) {
        setTimeout(() => {
          removeAlert(id);
        }, duration);
      }
    },
    [removeAlert]
  );

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 w-full max-w-md pointer-events-none">
        <div className="pointer-events-auto flex flex-col gap-2">
          {alerts.map((alert) => (
            <Alert key={alert.id} {...alert} />
          ))}
        </div>
      </div>
    </AlertContext.Provider>
  );
}

export function useAlert() {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
}
